/**
 * FINDIFY AUTOCOMPLETE - Bulletproof Version
 * Yksinkertainen ja robusti ratkaisu
 */

const initFindifyAutocompleteEvents = () => {
  // Globaali tila - säilyy renderöintien välillä
  window._findify = window._findify || {
    isOpen: false,
    currentQuery: '',
    activeInput: null,
    requestId: 0,
    isClearing: false,  // Lippu estämään looppi
    isTyping: false,    // Lippu joka seuraa onko käyttäjä kirjoittamassa
    typingTimer: null   // Timer tyhjentämiseen
  };

  const state = window._findify;

  // ==========================================================================
  // HELPER FUNKTIOT
  // ==========================================================================

  const getAutocomplete = () => document.querySelector('.findify-autocomplete');

  const getMobileInput = () => document.querySelector('[data-findify-mobile-input]');

  const getMobileInputPortal = () => document.querySelector('#findify-mobile-input-portal');

  const isMobile = () => window.innerWidth <= 768;

  const DELAY_MS = isMobile() ? 350 : 250; // Optimoitu: nopea haku, mutta ei liikaa API-kutsuja
  let debounceTimer = null;

  const getSearchUrl = (query) => {
    const root = window.Shopify?.routes?.root || '';
    return `${root}search?q=${encodeURIComponent(query)}`;
  };

  // ==========================================================================
  // MODAALIN HALLINTA
  // ==========================================================================

  const showModal = () => {
    const ac = getAutocomplete();
    if (!ac) {
      console.warn('Findify: Autocomplete element not found');
      return;
    }

    state.isOpen = true;
    ac.classList.remove('findify-hidden');
    document.body.classList.add('findify-modal-open');

    // iOS FIX: Näytä static input mobiilissa
    if (isMobile()) {
      const staticInput = getMobileInputPortal();
      if (staticInput) {
        staticInput.classList.remove('findify-hidden');
      }
    }

    // Luo overlay jos ei ole
    if (!document.querySelector('.findify-modal-overlay')) {
      const overlay = document.createElement('div');
      overlay.className = 'findify-modal-overlay';
      overlay.addEventListener('click', closeModal);

      // Aseta overlay alkamaan headerin alapuolelta (vain desktopilla)
      if (!isMobile()) {
        const header = document.querySelector('store-header, .shopify-section-header, #shopify-section-header, [id^="shopify-section-header"], header.header, .header');
        if (header) {
          const headerRect = header.getBoundingClientRect();
          overlay.style.top = `${headerRect.bottom}px`;
        }
      }

      document.body.appendChild(overlay);
    }

    // Positio
    if (isMobile()) {
      ac.style.top = '0';
      ac.style.height = '100%';
    } else if (state.activeInput) {
      const rect = state.activeInput.getBoundingClientRect();
      ac.style.top = `${rect.bottom + 2}px`;
    }
  };

  const closeModal = () => {
    // Kasvata requestId jotta pending API-kutsut ignoroidaan
    state.requestId++;

    // Peruuta pending debounce
    clearTimeout(debounceTimer);
    debounceTimer = null;

    const ac = getAutocomplete();
    if (ac && ac.classList) {
      ac.classList.add('findify-hidden');
    }

    // iOS FIX: Piilota static input mobiilissa
    if (isMobile()) {
      const staticInput = getMobileInputPortal();
      if (staticInput) {
        staticInput.classList.add('findify-hidden');
      }
    }

    state.isOpen = false;
    state.currentQuery = '';
    document.body.classList.remove('findify-modal-open');

    const overlay = document.querySelector('.findify-modal-overlay');
    if (overlay) overlay.remove();

    // Tyhjennä mobiili-input
    const mobileInput = getMobileInput();
    if (mobileInput && mobileInput.value !== '') {
      mobileInput.value = '';
    }

    // Tyhjennä header-input ja triggeröi Shopifyn reset-logiikka
    if (state.activeInput) {
      if (state.activeInput.value !== '') {
        // Aseta lippu jotta oma handler ei triggeröidy
        state.isClearing = true;
        state.activeInput.value = '';
        // Dispatch input event jotta Shopifyn hakukenttä päivittyy (piilottaa X-napin)
        state.activeInput.dispatchEvent(new Event('input', { bubbles: true }));
        state.isClearing = false;
      }
      state.activeInput.blur();
    }

    // Varmista että body scroll toimii
    document.body.style.overflow = '';
  };

  // ==========================================================================
  // HAKU
  // ==========================================================================

  const doSearch = async (query) => {
    // Salli tyhjä haku - Findify palauttaa trending-tulokset
    const searchQuery = query || '';

    // Tarkista että findify on käytettävissä
    if (typeof findify === 'undefined' || !findify.autocomplete) {
      console.warn('Findify: API not available');
      return;
    }

    const thisRequest = ++state.requestId;

    try {
      const response = await findify.autocomplete.api(searchQuery);

      // Tarkista onko tämä vielä uusin pyyntö
      if (thisRequest !== state.requestId) return;

      // iOS FIX: Input on nyt erillään renderöitävästä alueesta, joten se EI katoa!
      // Renderöi tulokset - input pysyy samana elementtinä
      await findify.autocomplete.render(response, findify.autocomplete.liquid);

      // KRIITTINEN: Renderöinti resetoi DOM:n, joten pakota modaali takaisin auki
      if (state.isOpen) {
        const ac = getAutocomplete();
        if (ac && ac.classList) {
          ac.classList.remove('findify-hidden');

          // Palauta positio
          if (isMobile()) {
            ac.style.top = '0';
            ac.style.height = '100%';
          } else if (state.activeInput) {
            try {
              const rect = state.activeInput.getBoundingClientRect();
              ac.style.top = `${rect.bottom + 2}px`;
            } catch (e) {
              // Input might be detached
            }
          }
        }
      }

      // Alusta suggestion-klikkit
      initSuggestionClicks();

    } catch (err) {
      console.error('Findify API error:', err);
    }
  };

  const debouncedSearch = (query) => {
    // Merkitse että käyttäjä kirjoittaa
    state.isTyping = true;
    clearTimeout(state.typingTimer);

    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      // Haku on valmis, käyttäjä ei enää kirjoita
      state.isTyping = false;
      doSearch(query);
    }, DELAY_MS);

    // Tyhjennä isTyping jos ei ole kirjoitettu X aikaan (pidempi aika varmuuden vuoksi)
    state.typingTimer = setTimeout(() => {
      state.isTyping = false;
    }, DELAY_MS + 500);
  };

  const navigateToSearch = (query) => {
    if (query && query.trim()) {
      window.location.href = getSearchUrl(query);
    }
  };

  // ==========================================================================
  // EVENT HANDLERS
  // ==========================================================================

  // Header input - focus
  const onHeaderInputFocus = (e) => {
    state.activeInput = e.target;
    showModal();
    
    // Hae tulokset - tyhjällä haulla tulee trending, muuten hakutulokset
    const query = e.target.value || '';
    state.currentQuery = query;
    doSearch(query);
  };

  // Header input - input event
  const onHeaderInputChange = (e) => {
    // Ohita jos olemme itse tyhjentämässä kenttää
    if (state.isClearing) return;

    // Merkitse heti että käyttäjä kirjoittaa
    state.isTyping = true;
    clearTimeout(state.typingTimer);

    const query = e.target.value;
    state.currentQuery = query;

    // Synkronoi mobiili-inputiin VAIN jos arvo on eri
    const mobileInput = getMobileInput();
    if (mobileInput && mobileInput !== e.target && mobileInput.value !== query) {
      mobileInput.value = query;
    }

    // Hae tulokset (tyhjällä haulla tulee trending-tulokset)
    debouncedSearch(query);
  };

  // Header input - keydown
  const onHeaderInputKeydown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      navigateToSearch(e.target.value);
    } else if (e.key === 'Escape') {
      closeModal();
    }
  };

  // Mobiili input - input event (käytetään event delegatiota)
  const onMobileInputChange = (e) => {
    // Merkitse heti että käyttäjä kirjoittaa
    state.isTyping = true;
    clearTimeout(state.typingTimer);

    const query = e.target.value;
    state.currentQuery = query;

    // Synkronoi header-inputiin VAIN jos arvo on eri
    if (state.activeInput && state.activeInput !== e.target && state.activeInput.value !== query) {
      state.activeInput.value = query;
    }

    // Hae tulokset (tyhjällä haulla tulee trending-tulokset)
    debouncedSearch(query);
  };

  // Mobiili input - keydown
  const onMobileInputKeydown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      navigateToSearch(e.target.value);
    } else if (e.key === 'Escape') {
      closeModal();
    }
  };

  // ==========================================================================
  // SUGGESTION KLIKKIT
  // ==========================================================================

  const initSuggestionClicks = () => {
    document.querySelectorAll('.findify-autocomplete [data-findify-suggestion]').forEach(el => {
      if (el._findifyBound) return;
      el._findifyBound = true;

      el.addEventListener('click', (e) => {
        e.preventDefault();
        const suggestion = el.textContent || el.innerText;
        navigateToSearch(suggestion);
      });
    });
  };

  // ==========================================================================
  // ALUSTUS - HEADER INPUTIT
  // ==========================================================================

  const initHeaderInputs = () => {
    document.querySelectorAll("input[name='q']").forEach(input => {
      // Ohita mobiili-input
      if (input.hasAttribute('data-findify-mobile-input')) return;
      
      // Älä alusta uudelleen
      if (input._findifyBound) return;
      input._findifyBound = true;

      input.addEventListener('focus', onHeaderInputFocus);
      input.addEventListener('input', onHeaderInputChange);
      input.addEventListener('keydown', onHeaderInputKeydown);
    });
  };

  // ==========================================================================
  // ALUSTUS - EVENT DELEGATION (mobiili + close)
  // ==========================================================================

  const initEventDelegation = () => {
    if (window._findifyDelegationInit) return;
    window._findifyDelegationInit = true;

    // Mobiili input - input event
    document.addEventListener('input', (e) => {
      if (e.target.matches('[data-findify-mobile-input]')) {
        onMobileInputChange(e);
      }
    }, true);

    // Mobiili input - keydown
    document.addEventListener('keydown', (e) => {
      if (e.target.matches('[data-findify-mobile-input]')) {
        onMobileInputKeydown(e);
      }
    }, true);

    // Close-napit
    document.addEventListener('click', (e) => {
      if (e.target.closest('.findify-close-autocomplete') || 
          e.target.closest('.findify-mobile-close')) {
        e.preventDefault();
        closeModal();
      }
    }, true);
  };

  // ==========================================================================
  // MOBIILI INPUTIN FOKUS
  // ==========================================================================

  const focusMobileInput = () => {
    if (!isMobile()) return;

    const mobileInput = getMobileInput();
    if (mobileInput && state.isOpen && !state.isTyping) {
      // Synkronoi arvo VAIN jos input ei ole aktiivinen JA käyttäjä ei ole kirjoittamassa
      if (state.currentQuery && document.activeElement !== mobileInput) {
        mobileInput.value = state.currentQuery;
      }

      // ÄLÄ palauta fokusta automaattisesti - anna käyttäjän hallita fokusta
      // setTimeout(() => {
      //   mobileInput.focus();
      // }, 50);
    }
  };

  // ==========================================================================
  // KÄYNNISTYS
  // ==========================================================================

  // Varmista että autocomplete on kiinni lähtötilanteessa (estää FOUC)
  // Mutta vain jos modaali ei ole jo auki (re-render ei sulje sitä)
  if (!state.isOpen) {
    try { closeModal(); } catch (e) {}
  }

  initHeaderInputs();
  initEventDelegation();
  initSuggestionClicks();
  focusMobileInput();
};

// Varmista että alustetaan kun DOM on valmis
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFindifyAutocompleteEvents);
} else {
  initFindifyAutocompleteEvents();
}